import React, { useState, useEffect } from "react";
import { useFormik } from 'formik';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField,
    Button, Box, Typography, FormControl, InputLabel, Select, MenuItem,
    InputAdornment, IconButton, Card, CardContent, Stack, Divider, CircularProgress,
} from '@mui/material';
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import Swal from 'sweetalert2';
import api from 'core/interceptors/api';
import KeywordsInput from 'layouts/tables/Product_Details/KeywordsInput';
import validationSchemaAdd from './add_vendor_schema';
import {
    CheckCircle as CheckCircleIcon,
    PhotoLibrary as PhotoLibraryIcon,
    Notifications as NotificationsIcon,
    CalendarMonth as CalendarMonthIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Star as StarIcon,
    Diamond as DiamondIcon,
    AttachMoney as AttachMoneyIcon,
    CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { keyframes } from '@mui/system';


const business_type = [
    { id: 1, category_name: 'Retail' },
    { id: 2, category_name: 'Wholesale' },
    { id: 3, category_name: 'Retail & Wholesale' },
];

const fileUploadLableStyle = {
    marginBottom: '8px',
    display: 'block',
    fontWeight: 'bold',
};

const fadeIn = keyframes`
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
`;

const FeatureItem = ({ icon, text, iconColor }) => (
    <Stack direction="row" alignItems="center" spacing={1.5}>
        {React.cloneElement(icon, { sx: { color: iconColor, fontSize: 'small' } })}
        <Typography variant="body2" color="text.secondary">{text}</Typography>
    </Stack>
);

const AddVendorModal = ({ open, handleClose, data }) => {
    const [duplicateErrors, setDuplicateErrors] = useState({
        email: "",
        mobile_number: "",
    });
    const [loading, setLoading] = useState(false);
    const [paymentPlans, setPaymentPlans] = useState([]);
    const [showPassword, setShowPassword] = useState(false);
    const [couponCode, setCouponCode] = useState('');
    const [couponError, setCouponError] = useState('');
    const [discount, setDiscount] = useState(0);
    const [validCoupon, setValidCoupon] = useState(false);
    const referredByCode = localStorage.getItem("code");



    useEffect(() => {
        const fetchPaymentPlans = async () => {
            try {
                const res = await api.get("/payment_plans/get");
                const vendorPlans = (res.data || []).filter(p => p.plan_type === 'vendor');
                setPaymentPlans(vendorPlans);
            } catch (err) {
                console.error("Failed to fetch payment plans:", err);
                setPaymentPlans([]);
            }
        };
        fetchPaymentPlans();
    }, []);

    const PaymentPlanCard = ({ plan, isSelected, onClick }) => {
        let cardBgColor = '#ffffff';
        let cardBorderColor = 'grey.300';
        let accentColor = '#607d8b';
        let gradientOverlay = 'none';
        let textColor = 'text.primary';
        let buttonColor = 'primary';
        let featureIconColor = 'action';

        if (plan.name.toLowerCase().includes('basic')) {
            cardBgColor = '#e0f7fa';
            cardBorderColor = '#00bcd4';
            accentColor = '#0097a7';
            textColor = '#006064';
            gradientOverlay = 'linear-gradient(135deg, rgba(0,188,212,0.1), rgba(0,150,136,0.15))';
            buttonColor = 'info';
            featureIconColor = '#00acc1';
        } else if (plan.name.toLowerCase().includes('standard')) {
            cardBgColor = '#e8f5e9';
            cardBorderColor = '#4caf50';
            accentColor = '#388e3c';
            textColor = '#1b5e20';
            gradientOverlay = 'linear-gradient(135deg, rgba(76,175,80,0.1), rgba(67,160,71,0.15))';
            buttonColor = 'success';
            featureIconColor = '#66bb6a';
        } else if (plan.name.toLowerCase().includes('premium') || plan.name.toLowerCase().includes('pro')) {
            cardBgColor = '#fff3e0';
            cardBorderColor = '#ff9800';
            accentColor = '#f57c00';
            textColor = '#e65100';
            gradientOverlay = 'linear-gradient(135deg, rgba(255,152,0,0.1), rgba(255,112,67,0.15))';
            buttonColor = 'warning';
            featureIconColor = '#ffb300';
        } else if (plan.name.toLowerCase().includes('enterprise')) {
            cardBgColor = '#ede7f6';
            cardBorderColor = '#673ab7';
            accentColor = '#5e35b1';
            textColor = '#311b92';
            gradientOverlay = 'linear-gradient(135deg, rgba(103,58,183,0.1), rgba(81,45,168,0.15))';
            buttonColor = 'secondary';
            featureIconColor = '#9c27b0';
        }

        const selectedShadow = `0 8px 25px ${accentColor}80, 0 0 0 3px ${accentColor}`;
        const hoverShadow = '0 4px 15px rgba(0,0,0,0.25)';

        return (
            <Card
                variant="outlined"
                onClick={onClick}
                sx={{
                    borderColor: isSelected ? accentColor : cardBorderColor,
                    boxShadow: isSelected ? selectedShadow : '0 2px 8px rgba(0,0,0,0.1)',
                    borderWidth: isSelected ? 3 : 1,
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'all 0.3s ease-in-out',
                    backgroundColor: isSelected ? cardBgColor : '#ffffff',
                    backgroundImage: gradientOverlay,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    '&:hover': {
                        boxShadow: hoverShadow,
                        borderColor: accentColor,
                        transform: 'translateY(-5px)',
                    },
                }}
            >
                {isSelected && (
                    <CheckCircleIcon sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        color: accentColor,
                        fontSize: 30,
                        animation: `${fadeIn} 0.3s ease-in-out`,
                        zIndex: 1,
                    }} />
                )}
                <CardContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h5" component="div" sx={{ color: textColor, fontWeight: 'bold' }}>{plan.name}</Typography>
                        <Typography sx={{ my: 1, fontSize: 13, color: 'text.secondary' }}>{plan.description}</Typography>
                        <Divider sx={{ my: 2, borderColor: accentColor + '60' }} />
                        <Stack spacing={1.5} my={2}>
                            <FeatureItem icon={<PhotoLibraryIcon />} text={`${plan.no_of_images_upload} Image Uploads`} iconColor={featureIconColor} />
                            <FeatureItem icon={<NotificationsIcon />} text={`${plan.no_of_notifications} Notifications`} iconColor={featureIconColor} />
                            <FeatureItem icon={<CalendarMonthIcon />} text={`${plan.period} Plan`} iconColor={featureIconColor} />
                            {plan.name.toLowerCase().includes('premium') && <FeatureItem icon={<StarIcon />} text="Priority Support" iconColor="gold" />}
                            {plan.name.toLowerCase().includes('enterprise') && <FeatureItem icon={<DiamondIcon />} text="Custom Features & Analytics" iconColor="teal" />}
                            {plan.name.toLowerCase().includes('pro') && <FeatureItem icon={<CloudUploadIcon />} text="Unlimited Storage" iconColor="#42a5f5" />}
                            {plan.name.toLowerCase().includes('standard') && <FeatureItem icon={<AttachMoneyIcon />} text="Basic Reporting" iconColor="#dce775" />}
                        </Stack>
                    </Box>
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <Typography variant="h4" fontWeight="bold" sx={{ color: accentColor }}>
                            ₹{plan.amount}
                        </Typography>
                        <MDButton
                            variant="contained"
                            color={buttonColor}
                            sx={{ mt: 2 }}
                            onClick={onClick}
                        >
                            {isSelected ? "Selected" : "Choose Plan"}
                        </MDButton>
                    </Box>
                </CardContent>
            </Card>
        );
    };

    const formik = useFormik({
        initialValues: {
            business_name: '', business_type: '', business_category: '', owner_name: '',
            email: '', mobile_number: '', password: '', referred_by_code: referredByCode || '', account_no: '', ifsc_code: '',
            passbook_name: '', bank_name: '', branch_name: '', pan_card_number: '',
            gst: '', payment_plan_id: '', user_proof_doc: [], keywords: [],
            vendor_photo: null,
            shop_photo: null,
            gps_photo: null,
            no_of_notifications: 0,
            no_of_images_upload: 0,
        },
        validationSchema: validationSchemaAdd,
        validateOnMount: true, 
        onSubmit: async (values) => {
            setLoading(true); // Start loading when form is submitted
            try {
                const plan = paymentPlans.find(p => p.id.toString() === values.payment_plan_id);
                if (!plan) {
                    Swal.fire("Error", "Selected payment plan not found.", "error");
                    return; // Loading will be handled by the finally block
                }
                const selectedPlanAmount = plan.amount;
                const payableAmount = validCoupon ? getDiscountedAmount(selectedPlanAmount) : selectedPlanAmount;
                // 1. Initiate Razorpay Payment
                const paymentInit = await api.post("/razor_payments/", {
                    amount_paid: payableAmount,
                    payment_method: "Online",
                    user_id: values.email,
                    type_of_vendor: "vendor"
                });

                const { razorpay_order_id, razorpay_key_id, amount, payment_id } = paymentInit.data;

                const options = {
                    key: razorpay_key_id,
                    amount,
                    currency: "INR",
                    order_id: razorpay_order_id,
                    name: `${plan.name} Plan`,
                    description: `Subscription Payment for Vendor`,
                    image: "/assets/images/logo.png",
                    handler: async function (response) {
                        setLoading(true);
                        try {
                            // 2. Update payment status after successful Razorpay callback
                            await api.put(`/razor_payments/${payment_id}`, {
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                                payment_status: "success",
                                payment_method: "Online",
                                paid_at: new Date().toISOString(),
                                user_id: values.email,
                                type_of_vendor: "vendor",
                                plan_name: plan.name,
                                plan_validity_days:plan.period || plan.plan_period,
                            });

                            // 3. Prepare FormData for vendor registration, including plan-specific features
                            const formData = new FormData();
                            Object.entries(values).forEach(([key, value]) => {
                                if (key === 'user_proof_doc' && Array.isArray(value)) {
                                    value.forEach((file, index) => {
                                        formData.append(`${key}_${index}`, file);
                                    });
                                } else if (value instanceof File) {
                                    formData.append(key, value);
                                } else if (Array.isArray(value)) {
                                    if (key === 'keywords') {
                                        formData.append(key, value.join(','));
                                    } else {
                                        formData.append(key, JSON.stringify(value));
                                    }
                                } else {
                                    formData.append(key, value);
                                }
                            });

                            // Append no_of_notifications and no_of_images_upload from the selected plan
                            formData.append("no_of_notifications", plan.no_of_notifications);
                            formData.append("no_of_photos", plan.no_of_images_upload);


                            const res = await api.post('/vendor_register/register_vendor', formData);
                            if (res.data.status === 1) {
                                // Vendor registered successfully
                                try {
                                    // Prepare data needed for incentive earning update
                                    // `res.data` may contain vendor info — adjust if your API returns the registered vendor ID
                                    const referralId = localStorage.getItem('code') || ''; // Replace with actual ID from your API response
                                    const paidAmount = payableAmount;                   // The same paid amount you passed earlier
                                    // Admin info can be from localStorage or your auth context
                                    const adminEmail = localStorage.getItem('email') || '';
                                    const adminRole = localStorage.getItem('role') || '';

                                    // Call incentive earnings API
                                    await api.post('/incentive_earning', {
                                        referral_id: referralId,
                                        paid_amount: paidAmount,
                                        admin_email: adminEmail,
                                        role: adminRole,
                                    });

                                    Swal.fire("Success", "Vendor registered successfully", "success");
                                } catch (incentiveError) {
                                    console.error("Incentive earnings update failed:", incentiveError);

                                }

                                formik.resetForm();
                                handleClose();
                            } else {
                                Swal.fire("Error", res.data.message || "Vendor registration failed", "error");
                            }

                        } catch (err) {
                            console.error("Error during registration after payment:", err);
                            Swal.fire("Error", "Something went wrong during registration.", "error");
                        } finally {
                            setLoading(false); // Crucial: Stop loading after registration attempt (success or failure)
                        }
                    },
                    prefill: {
                        name: values.owner_name,
                        contact: values.mobile_number,
                        email: values.email
                    },
                    theme: { color: "#F37254" }
                };

                const rzp = new window.Razorpay(options);
                rzp.on('razorpay.modal.close', () => {
                    console.log("Razorpay modal closed event triggered.");
                    Swal.fire("Payment Cancelled", "The payment process was cancelled.", "info");
                    // No need to set loading to false here, it will be handled by the outer finally
                });
                rzp.open();

            } catch (err) {
                console.error("Error in payment initiation or network issue:", err);
                Swal.fire("Error", err.response?.data?.message || "Failed to initiate payment. Please try again.", "error");
            } finally {
                // This is the key change: Ensure loading is always set to false
                // after the *initial* onSubmit promise settles, whether it resolves
                // with `rzp.open()` or rejects due to an error before that.
                // If the handler is called, it takes over loading, and its finally
                // sets it to false. If the handler is *not* called (e.g., modal closed),
                // this outer finally will catch it.
                setLoading(false);
            }
        }
    });
    useEffect(() => {
  console.log("Formik Errors:", formik.errors);
  console.log("Formik Values:", formik.values);
  console.log("Formik isValid:", formik.isValid);
}, [formik.errors, formik.values, formik.isValid]);

    const validateCoupon = async () => {
        console.log("validateCoupon function started!"); // This confirms button click

        try {
            const res = await api.get("/coupons/"); // Attempt to fetch coupons
            const fetchedCouponList = res.data?.coupons || []; // Store fetched data in a new variable
            console.log("Available Coupons (after fetch):", fetchedCouponList); // Log the *actual* fetched list

            const matched = fetchedCouponList.find(c => c.coupon_id === couponCode.trim().toUpperCase());

            if (!matched) {
                throw new Error("Invalid coupon"); // Throw a specific error for clarity
            }

            const now = new Date();
            const start = new Date(matched.start_date);
            const end = new Date(matched.end_date);

            if (now >= start && now <= end) {
                setDiscount(matched.percentage);
                setValidCoupon(true);

            } else {
                setDiscount(0);
                setValidCoupon(false);

            }
        } catch (err) {
            setDiscount(0);
            setValidCoupon(false);
            console.error("Error during coupon validation or fetch:", err); // Log the full error object!
            let errorMessage = "An unexpected error occurred.";
            if (err.message === "Invalid coupon") {
                errorMessage = "Coupon code not found.";
            } else if (err.response && err.response.data && err.response.data.message) {
                errorMessage = err.response.data.message; // From API response error
            } else if (err.message) {
                errorMessage = err.message; // General JavaScript error message
            }
            Swal.fire("Alert", errorMessage, "info");
        }
    };
    const getDiscountedAmount = (amount) => {
        return Math.round(amount - (amount * discount) / 100);
    };
    const checkDuplicateEmail = async (email) => {
        try {
            const res = await api.post("/vendor_register/check_duplicate_email", { email });
            return res.data.exists;
        } catch (err) {
            console.error("Email duplicate check failed:", err);
            return false;
        }
    };

    const checkDuplicateMobile = async (mobile_number) => {
        try {
            const res = await api.post("/vendor_register/check_duplicate_mobile", { mobile_number });
            return res.data.exists;
        } catch (err) {
            console.error("Mobile duplicate check failed:", err);
            return false;
        }
    };

    const handleFileChange = (event, fieldName) => {
        const files = event.target.files;

        if (files && files.length > 0) {
            if (fieldName === 'user_proof_doc') {
                const fileArray = Array.from(files);
                formik.setFieldValue(fieldName, fileArray);
            } else {
                formik.setFieldValue(fieldName, files[0]);
            }
        }
    };

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const handleCancel = () => {
        formik.resetForm(); // Reset all formik values, errors, and touched state
        setDuplicateErrors({ email: "", mobile_number: "" }); // Reset duplicate errors
        setLoading(false);
        setCouponCode("");
        setDiscount(0);
        setCouponError("");
        handleClose(); // Close the modal
    };

    return (
        <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="lg">
            <DialogTitle>Add Vendor</DialogTitle>
            <form onSubmit={(e) => {
                e.preventDefault();
                formik.handleSubmit(e);
            }}>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Box sx={{ backgroundColor: '#e3f2fd', p: 2, borderRadius: 1, mb: 2, elevation: 3 }}>
                                <Typography variant="h6" gutterBottom>Basic Information</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Business Name"
                                            name="business_name"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.business_name}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.business_name && Boolean(formik.errors.business_name)}
                                            helperText={formik.touched.business_name && formik.errors.business_name}
                                            InputProps={{
                                                style: { borderColor: 'black' },
                                            }}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth margin="normal">
                                            <InputLabel id="business_type">Business Type</InputLabel>
                                            <Select
                                                labelId="business-type-label"
                                                label="Business Type"
                                                name="business_type"
                                                value={formik.values.business_type}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                sx={{ height: '45px' }}
                                                error={formik.touched.business_type && Boolean(formik.errors.business_type)}
                                            >
                                                {business_type.map((category) => (
                                                    <MenuItem key={category.id} value={category.category_name}>
                                                        {category.category_name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {formik.touched.business_type && formik.errors.business_type && (
                                                <Typography color="error" variant="body2">{formik.errors.business_type}</Typography>
                                            )}
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth margin="normal">
                                            <InputLabel id="business-category-label">Business Category</InputLabel>
                                            <Select
                                                labelId="business-category-label"
                                                label="Business Category"
                                                name="business_category"
                                                value={formik.values.business_category}
                                                onChange={(e) => {
                                                    formik.handleChange(e);
                                                    if (e.target.value !== "Others") {
                                                        formik.setFieldValue("custom_business_category", "");
                                                    }
                                                }}
                                                onBlur={formik.handleBlur}
                                                sx={{ height: '45px' }}
                                                error={formik.touched.business_category && Boolean(formik.errors.business_category)}
                                            >
                                                {data.length > 0 ? (
                                                    data.map((datas) => (
                                                        <MenuItem key={datas.id} value={datas.business_category}>
                                                            {datas.business_category}
                                                        </MenuItem>
                                                    ))
                                                ) : (
                                                    <MenuItem disabled>No categories available</MenuItem>
                                                )}
                                            </Select>
                                            {formik.touched.business_category && formik.errors.business_category && (
                                                <Typography color="error" variant="body3">
                                                    {formik.errors.business_category}
                                                </Typography>
                                            )}
                                        </FormControl>
                                    </Grid>


                                    <Grid item xs={12} sm={6}>
                                        <KeywordsInput
                                            keywords={formik.values.keywords}
                                            onChange={(newKeywords) => {
                                                formik.setFieldValue("keywords", newKeywords);
                                                formik.setFieldTouched("keywords", true);
                                            }}
                                            onBlur={() => formik.setFieldTouched("keywords", true)}
                                            touched={formik.touched.keywords}
                                            error={formik.errors.keywords}
                                        />
                                    </Grid>


                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Owner Name"
                                            name="owner_name"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.owner_name}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.owner_name && Boolean(formik.errors.owner_name)}
                                            helperText={formik.touched.owner_name && formik.errors.owner_name}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Email"
                                            name="email"
                                            value={formik.values.email}
                                            onChange={(e) => {
                                                formik.handleChange(e);
                                                setDuplicateErrors((prev) => ({ ...prev, email: "" }));
                                            }}
                                            onBlur={async (e) => {
                                                formik.handleBlur(e);
                                                const email = e.target.value;

                                                if (email && !formik.errors.email) {
                                                    const isDuplicate = await checkDuplicateEmail(email);
                                                    setDuplicateErrors((prev) => ({
                                                        ...prev,
                                                        email: isDuplicate ? "Email is already registered" : "",
                                                    }));
                                                }
                                            }}
                                            fullWidth
                                            margin="normal"
                                            error={
                                                (formik.touched.email && Boolean(formik.errors.email)) ||
                                                (formik.touched.email && Boolean(duplicateErrors.email))
                                            }
                                            helperText={
                                                (formik.touched.email && formik.errors.email) ||
                                                (formik.touched.email && duplicateErrors.email)
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Mobile Number"
                                            name="mobile_number"
                                            value={formik.values.mobile_number}
                                            onChange={(e) => {
                                                formik.handleChange(e);
                                                setDuplicateErrors((prev) => ({ ...prev, mobile_number: "" }));
                                            }}
                                            onBlur={async (e) => {
                                                formik.handleBlur(e);
                                                const number = e.target.value;

                                                if (number && number.length === 10 && !formik.errors.mobile_number) {
                                                    const isDuplicate = await checkDuplicateMobile(number);
                                                    setDuplicateErrors((prev) => ({
                                                        ...prev,
                                                        mobile_number: isDuplicate ? "Mobile number already registered" : "",
                                                    }));
                                                }
                                            }}
                                            fullWidth
                                            margin="normal"
                                            error={
                                                (formik.touched.mobile_number && Boolean(formik.errors.mobile_number)) ||
                                                (formik.touched.mobile_number && Boolean(duplicateErrors.mobile_number))
                                            }
                                            helperText={
                                                (formik.touched.mobile_number && formik.errors.mobile_number) ||
                                                (formik.touched.mobile_number && duplicateErrors.mobile_number)
                                            }
                                        />
                                    </Grid>



                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Password"
                                            name="password"
                                            type={showPassword ? 'text' : 'password'}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.password}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.password && Boolean(formik.errors.password)}
                                            helperText={formik.touched.password && formik.errors.password}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={handleClickShowPassword}
                                                            onMouseDown={handleMouseDownPassword}
                                                            edge="end"
                                                        >
                                                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>

                        {/* Bank Details */}
                        <Grid item xs={12}>
                            <Box sx={{ backgroundColor: '#f3e5f5', p: 2, borderRadius: 1, mb: 2 }}>
                                <Typography variant="h6" gutterBottom>Bank Details</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6} sm={6}>
                                        <TextField
                                            label="Account Number"
                                            name="account_no"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.account_no}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.account_no && Boolean(formik.errors.account_no)}
                                            helperText={formik.touched.account_no && formik.errors.account_no}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="IFSC Code"
                                            name="ifsc_code"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.ifsc_code}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.ifsc_code && Boolean(formik.errors.ifsc_code)}
                                            helperText={formik.touched.ifsc_code && formik.errors.ifsc_code}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Passbook Name"
                                            name="passbook_name"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.passbook_name}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.passbook_name && Boolean(formik.errors.passbook_name)}
                                            helperText={formik.touched.passbook_name && formik.errors.passbook_name}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Bank Name"
                                            name="bank_name"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.bank_name}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.bank_name && Boolean(formik.errors.bank_name)}
                                            helperText={formik.touched.bank_name && formik.errors.bank_name}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Branch Name"
                                            name="branch_name"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.branch_name}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.branch_name && Boolean(formik.errors.branch_name)}
                                            helperText={formik.touched.branch_name && formik.errors.branch_name}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Pan Card Number"
                                            name="pan_card_number"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.pan_card_number}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.pan_card_number && Boolean(formik.errors.pan_card_number)}
                                            helperText={formik.touched.pan_card_number && formik.errors.pan_card_number}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="GST Number"
                                            name="gst"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.gst}
                                            fullWidth
                                            margin="normal"
                                            error={formik.touched.gst && Boolean(formik.errors.gst)}
                                            helperText={formik.touched.gst && formik.errors.gst}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                        {/* Images */}
                        <Grid item xs={12}>
                            <Box sx={{ backgroundColor: '#ebfeff', p: 2, borderRadius: 1 }}>
                                <Typography variant="h6" gutterBottom>Images</Typography>
                                <Grid container spacing={2}>

                                    {/* Vendor Photo */}
                                    <Grid item xs={12} sm={6}>
                                        <MDBox margin="normal" fullWidth>
                                            <InputLabel htmlFor="vendor_photo" style={fileUploadLableStyle}>Vendor Photo</InputLabel>
                                            <MDButton component="label" color="warning">
                                                Upload Vendor Photo
                                                <input
                                                    type="file"
                                                    hidden
                                                    id="vendor_photo"
                                                    name="vendor_photo"
                                                    onChange={(event) => handleFileChange(event, 'vendor_photo')}
                                                    accept="image/*"
                                                />
                                            </MDButton>
                                            {formik.touched.vendor_photo && formik.errors.vendor_photo && (
                                                <Typography color="error" variant="body2">{formik.errors.vendor_photo}</Typography>
                                            )}
                                        </MDBox>
                                        {formik.values.vendor_photo instanceof File && (
                                            <MDBox display="flex" flexDirection="row" gap={2} mt={2}>
                                                <MDBox component="img"
                                                    src={URL.createObjectURL(formik.values.vendor_photo)}
                                                    alt="Vendor Photo"
                                                    width="100px"
                                                    height="100px"
                                                    style={{ objectFit: 'cover' }}
                                                />
                                            </MDBox>
                                        )}
                                    </Grid>

                                    {/* Shop Photo */}
                                    <Grid item xs={12} sm={6}>
                                        <MDBox margin="normal" fullWidth>
                                            <InputLabel htmlFor="shop_photo" style={fileUploadLableStyle}>Shop Photo With Nameboard</InputLabel>
                                            <MDButton variant="contained" component="label" color="warning">
                                                Upload Shop Photo
                                                <input
                                                    type="file"
                                                    hidden
                                                    id="shop_photo"
                                                    name="shop_photo"
                                                    onChange={(event) => handleFileChange(event, 'shop_photo')}
                                                    accept="image/*"
                                                />
                                            </MDButton>
                                            {formik.touched.shop_photo && formik.errors.shop_photo && (
                                                <Typography color="error" variant="body2">{formik.errors.shop_photo}</Typography>
                                            )}
                                        </MDBox>
                                        {formik.values.shop_photo instanceof File && (
                                            <MDBox display="flex" flexDirection="row" gap={2} mt={2}>
                                                <MDBox component="img"
                                                    src={URL.createObjectURL(formik.values.shop_photo)}
                                                    alt="Shop Photo"
                                                    width="100px"
                                                    height="100px"
                                                    style={{ objectFit: 'cover' }}
                                                />
                                            </MDBox>
                                        )}
                                    </Grid>

                                    {/* GPS Shop Photo */}
                                    <Grid item xs={12} sm={6}>
                                        <MDBox margin="normal" fullWidth>
                                            <InputLabel htmlFor="gps_photo" style={fileUploadLableStyle}>Shop GPS Photo (OPTIONAL)</InputLabel>
                                            <MDButton variant="contained" component="label" color="warning">
                                                Upload GPS Photo
                                                <input
                                                    type="file"
                                                    hidden
                                                    id="gps_photo"
                                                    name="gps_photo"
                                                    onChange={(event) => handleFileChange(event, 'gps_photo')}
                                                    accept="image/*"
                                                />
                                            </MDButton>
                                            {formik.touched.gps_photo && formik.errors.gps_photo && (
                                                <Typography color="error" variant="body2">{formik.errors.gps_photo}</Typography>
                                            )}
                                        </MDBox>
                                        {formik.values.gps_photo instanceof File && (
                                            <MDBox display="flex" flexDirection="row" gap={2} mt={2}>
                                                <MDBox component="img"
                                                    src={URL.createObjectURL(formik.values.gps_photo)}
                                                    alt="Shop GPS Photo (OPTIONAL)"
                                                    width="100px"
                                                    height="100px"
                                                    style={{ objectFit: 'cover' }}
                                                />
                                            </MDBox>
                                        )}
                                    </Grid>

                                    {/* Gov. ID Proof (Multiple Uploads) */}
                                    <Grid item xs={12} sm={6}>
                                        <MDBox margin="normal" fullWidth>
                                            <InputLabel htmlFor="user_proof_doc" style={fileUploadLableStyle}>Gov. ID Proof (Front & Back)</InputLabel>
                                            <MDButton variant="contained" component="label" color="warning">
                                                Upload Gov. ID Proof
                                                <input
                                                    type="file"
                                                    hidden
                                                    id="user_proof_doc"
                                                    name="user_proof_doc"
                                                    multiple
                                                    accept="image/*"
                                                    onChange={(event) => handleFileChange(event, 'user_proof_doc')}
                                                />
                                            </MDButton>
                                            {formik.touched.user_proof_doc && formik.errors.user_proof_doc && (
                                                <Typography color="error" variant="body2">{formik.errors.user_proof_doc}</Typography>
                                            )}
                                        </MDBox>

                                        {Array.isArray(formik.values.user_proof_doc) && formik.values.user_proof_doc.length > 0 && (
                                            <MDBox display="flex" flexDirection="row" gap={2} mt={2}>
                                                {formik.values.user_proof_doc.map((file, index) => (
                                                    file && file instanceof Blob ? (
                                                        <MDBox key={index} component="img"
                                                            src={URL.createObjectURL(file)}
                                                            alt={`ID Proof ${index + 1}`}
                                                            width="100px"
                                                            height="100px"
                                                            style={{ objectFit: 'cover' }}
                                                        />
                                                    ) : null
                                                ))}
                                            </MDBox>
                                        )}

                                    </Grid>
                                    {/* Payment Plan Section */}
                                    <Grid item xs={12}>
                                        <Box sx={{ bgcolor: '#f3e5f5', p: 2, borderRadius: 2 }}>
                                            <Typography variant="h6" gutterBottom>Select a Payment Plan</Typography>
                                            <Grid container spacing={2}>
                                                {paymentPlans.length > 0 ? (
                                                    Array.isArray(paymentPlans) && paymentPlans.map((plan) => (
                                                        <Grid item xs={12} sm={6} md={4} key={plan.id}>
                                                            <PaymentPlanCard
                                                                plan={plan}
                                                                isSelected={formik.values.payment_plan_id === plan.id.toString()}
                                                               onClick={() => {
  formik.setFieldValue("payment_plan_id", plan.id.toString());
  formik.setFieldTouched("payment_plan_id", true); // ✅ IMPORTANT
}}

                                                            />
                                                        </Grid>
                                                    ))
                                                ) : (
                                                    <Grid item xs={12}><Typography>No Payment Plans Available</Typography></Grid>
                                                )}
                                            </Grid>
                                            {formik.touched.payment_plan_id && formik.errors.payment_plan_id && (
                                                <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                                                    {formik.errors.payment_plan_id}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Coupon Code"
                                            name="coupon_code"
                                            value={couponCode}
                                            onChange={(e) => setCouponCode(e.target.value)}
                                            fullWidth
                                            margin="normal"
                                            error={!!couponError}
                                            helperText={couponError}
                                            // Disable until a payment plan is selected
                                            disabled={!formik.values.payment_plan_id}
                                        />
                                        <Button
                                            variant="contained"
                                            color="info"
                                            onClick={validateCoupon}
                                            // Disable when loading, if code empty, or no plan selected
                                            disabled={loading || !couponCode || !formik.values.payment_plan_id}
                                            sx={{ mt: 1, ml: 2 }}
                                        >
                                            Apply
                                        </Button>

                                        {/* Discount message and discounted amount */}
                                        {validCoupon && formik.values.payment_plan_id && (
                                            <>
                                                <Typography color="success.main" mt={1}>
                                                    Coupon Applied: {discount}% off
                                                </Typography>
                                                <Typography mt={1} fontWeight={700}>
                                                    Discounted Amount:&nbsp;
                                                    ₹
                                                    {getDiscountedAmount(
                                                        paymentPlans.find(p => p.id.toString() === formik.values.payment_plan_id)?.amount || 0
                                                    )}
                                                </Typography>
                                            </>
                                        )}
                                    </Grid>


                                </Grid>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>


                <DialogActions>
                    <Button
                        onClick={handleCancel}
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <MDButton
                        type="submit"
                        color="success"
                        disabled={
                            loading ||
                            !formik.isValid ||
                            !!duplicateErrors.email ||
                            !!duplicateErrors.mobile_number ||
                            !formik.values.payment_plan_id
                        }
                    >
                        {loading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            "Save"
                        )}
                    </MDButton>

                </DialogActions>
            </form>
        </Dialog>
    );
};

export default AddVendorModal;